import React, { useEffect, useId, useState } from "react";
import { format } from "date-fns";
import { CheckCircle2Icon, LockIcon, Loader2Icon, RefreshCwIcon, Trash2Icon, UploadIcon } from "lucide-react";
import { useHackathon } from "../contexts/HackathonContext";
import type { VideoEntry } from "../types/hackathon";
import { formatBytes, formatDay } from "../utils/dates";
import { loadVideo } from "../utils/videoStore";
interface VideoUploadSlotProps {
  groupId: number;
  day: number;
  entry?: VideoEntry;
  locked: boolean;
  isToday: boolean;
  prompt?: string;
}
const MAX_BYTES = 1024 * 1024 * 1024;
export function VideoUploadSlot({
  groupId,
  day,
  entry,
  locked,
  isToday,
  prompt
}: VideoUploadSlotProps) {
  const {
    addVideo,
    removeVideo
  } = useHackathon();
  const inputId = useId();
  const [url, setUrl] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  const [pending, setPending] = useState<File | null>(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!entry) {
      setUrl(null);
      return;
    }
    let objectUrl: string | null = null;
    let cancelled = false;
    loadVideo(groupId, day).then(blob => {
      if (cancelled) return;
      if (blob) {
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
        setMissing(false);
      } else {
        setMissing(true);
      }
    });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [entry?.uploadedAt, groupId, day]);
  const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("video/")) return setError("Please choose a video file.");
    if (file.size > MAX_BYTES) return setError("Videos must be under 1 GB.");
    setError(null);
    setNote(entry?.note ?? "");
    setPending(file);
  };
  const submit = async () => {
    if (!pending) return;
    setSaving(true);
    try {
      await addVideo({
        groupId,
        day,
        fileName: pending.name,
        size: pending.size,
        note: note.trim(),
        uploadedAt: new Date().toISOString()
      }, pending);
      setPending(null);
    } catch {
      setError("Couldn't save the video. Browser storage may be full.");
    } finally {
      setSaving(false);
    }
  };
  const fileInput = <input id={inputId} type="file" accept="video/*" className="sr-only" onChange={pick} disabled={locked || saving} data-id="element-730" data-mp="21gbm9g720iq5" />;
  return <article className={`flex flex-col rounded-xl border bg-surface p-4 ${isToday ? "border-ink" : "border-line"}`} data-id="element-731" data-mp="2b8pkhyyxb77k">
      <header className="flex items-baseline justify-between gap-2" data-id="element-732" data-mp="26gz8zpo92dek">
        <h3 className="text-sm font-semibold" data-id="element-733" data-mp="1irmz0nl3m1b7">
          Day {day}
          {isToday && <span className="ml-2 rounded bg-bryellow px-1.5 py-0.5 text-[11px] font-bold text-navy" data-id="element-734" data-mp="1dt3dgoaf2rjd">Today</span>}
        </h3>
        <span className="text-xs text-muted" data-id="element-735" data-mp="3pg5hu1280ef3">{formatDay(day)}</span>
      </header>

      <div className="mt-3 flex flex-1 flex-col" data-id="element-736" data-mp="3u6vpjky4ctcy">
        {pending ? <div className="flex flex-1 flex-col" data-id="element-737" data-mp="3u8ynqs9fdssr">
            <p className="truncate text-sm font-medium" data-id="element-738" data-mp="3fjam1pxc0s4i">{pending.name}</p>
            <p className="text-xs text-muted" data-id="element-739" data-mp="3kh2mj8c1lmkb">{formatBytes(pending.size)}</p>
            <label className="mt-3 block text-xs font-medium text-muted" htmlFor={`${inputId}-note`} data-id="element-740" data-mp="3kezn5xp02h2x">
              What did the team accomplish?
            </label>
            <textarea id={`${inputId}-note`} rows={3} value={note} onChange={e => setNote(e.target.value)} className="mt-1 w-full resize-none rounded-lg border border-line px-2.5 py-2 text-sm outline-none focus:border-ink" data-id="element-741" data-mp="h03otvfohznd" />
            <div className="mt-auto flex justify-end gap-2 pt-3" data-id="element-742" data-mp="2nps0gmcna1xf">
              <button type="button" onClick={() => setPending(null)} className="px-3 py-1.5 text-sm text-muted hover:text-ink" data-id="element-743" data-mp="2rtq151hmbksl">
                Cancel
              </button>
              <button type="button" onClick={submit} disabled={saving} className="inline-flex items-center gap-1.5 rounded-lg bg-ink px-3 py-1.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-ink/85 disabled:bg-ink/50" data-id="element-744" data-mp="339csiac6wewo">
                {saving && <Loader2Icon className="h-3.5 w-3.5 animate-spin" aria-hidden="true" data-id="element-745" data-mp="3qxcktep5gddd" />}
                {saving ? "Saving" : "Save video"}
              </button>
            </div>
          </div> : entry ? <>
            {url ? <video src={url} controls preload="metadata" className="aspect-video w-full rounded-lg bg-navy" data-id="element-746" data-mp="3cywdpmndkkup" /> : <div className="flex aspect-video w-full items-center justify-center rounded-lg bg-paper px-4 text-center text-xs text-muted" data-id="element-747" data-mp="25lksl8lqyzle">
                {missing ? "Video file is stored on another device." : "Loading video…"}
              </div>}
            <p className="mt-2 flex items-center gap-1.5 text-xs text-muted" data-id="element-748" data-mp="l3a1mtu0g595">
              <CheckCircle2Icon className="h-3.5 w-3.5 text-brgreen" aria-hidden="true" data-id="element-749" data-mp="l17dyggbyo7l" />
              Uploaded {format(new Date(entry.uploadedAt), "MMM d, HH:mm")}
            </p>
            {entry.note && <p className="mt-1.5 text-sm" data-id="element-750" data-mp="uueiy075s275">{entry.note}</p>}
            <div className="mt-auto flex gap-1 pt-3" data-id="element-751" data-mp="zszjk0qbonw4">
              <label htmlFor={inputId} className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-muted transition-colors duration-150 hover:bg-paper hover:text-ink" data-id="element-752" data-mp="zuivxg8uyesm">
                <RefreshCwIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-753" data-mp="1dqo4t9oaxx1p" />
                Replace
              </label>
              {fileInput}
              <button type="button" onClick={() => window.confirm(`Delete the Day ${day} video?`) && removeVideo(groupId, day)} className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-muted transition-colors duration-150 hover:bg-paper hover:text-danger" data-id="element-754" data-mp="3fjc9zokiauk4">
                <Trash2Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-755" data-mp="vr3g3bavwz47" />
                Delete
              </button>
            </div>
          </> : locked ? <div className="flex flex-1 flex-col items-center justify-center rounded-lg bg-paper px-4 py-8 text-center" data-id="element-756" data-mp="15mwpiltb4chr">
            <LockIcon className="h-4 w-4 text-muted" aria-hidden="true" data-id="element-757" data-mp="10ntu7t34pkmz" />
            <p className="mt-2 text-xs text-muted" data-id="element-758" data-mp="1egjglymkkrkm">Opens {formatDay(day, "MMM d")}</p>
          </div> : <>
            <label htmlFor={inputId} className="flex flex-1 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-ink/20 px-4 py-6 text-center transition-colors duration-150 hover:border-ink/50 hover:bg-paper" data-id="element-759" data-mp="1afpwzhg5tyxj">
              <UploadIcon className="h-5 w-5 text-ink" aria-hidden="true" data-id="element-760" data-mp="231xwfot2kh55" />
              <span className="mt-2 text-sm font-semibold" data-id="element-761" data-mp="280izrx3mbgxt">Upload video</span>
              <span className="mt-0.5 text-xs text-muted" data-id="element-762" data-mp="1t2qlf89t4yl7">Up to 2 minutes · MP4 or MOV</span>
            </label>
            {fileInput}
            {prompt && <p className="mt-2 text-xs text-muted" data-id="element-763" data-mp="1y0icy16jiiov">Prompt: {prompt}</p>}
          </>}
        {error && <p className="mt-2 text-xs text-danger" role="alert" data-id="element-764" data-mp="1k8tznukk1o6b">
            {error}
          </p>}
      </div>
    </article>;
}